import { parseNumber, toFixedWithoutRounding } from '@/src/util/numbers';

import { TreatmentCalculator, Strategy } from './TreatmentCalculator';

import { LineEntry } from '../../types';
import { LineEntryForm } from '../../types/treatment';

export class InclusiveTreatmentCalculator extends TreatmentCalculator {
  static calculateTaxableAmount(amount: number, taxRate: number) {
    const taxMultipler = 1 + taxRate / 100;

    return amount / taxMultipler;
  }

  static calculateAmountBeforeTax(amount: number, taxRate: number) {
    return amount + +Number(amount * +Number((taxRate / 100).toFixed(2)));
  }

  static calculateSubtotalDisplay = (amount: number, tax: number) => {
    return amount + tax;
  };

  static calculateTotals({ lineItems }: { lineItems: LineEntryForm[] | LineEntry[] }) {
    let taxesSubTotal = 0;
    let subTotal = 0;
    let total = 0;

    for (const lineItem of lineItems) {
      const amount = parseNumber(lineItem.totalAmount || 0);
      const taxRate = super.getTaxRate(lineItem.taxCode);
      const taxableAmount = this.calculateTaxableAmount(amount, taxRate);
      const tax = super.calculateTax(taxableAmount, taxRate);

      subTotal += amount;
      taxesSubTotal += tax;
      total += amount;
    }

    return super.formatTotals({ subTotal, taxesSubTotal, total });
  }

  static calculateLineItems({
    lineItems,
    usePriceBeforeTax = false,
  }: {
    lineItems: LineEntryForm[] | LineEntry[];
    usePriceBeforeTax?: boolean;
    strategy?: Strategy;
  }) {
    return lineItems.map((lineItem) => {
      const amount = parseNumber(lineItem.totalAmount || 0);
      const taxRate = super.getTaxRate(lineItem.taxCode);
      const taxableAmount = this.calculateTaxableAmount(amount, taxRate);
      const priceBeforeTax = this.calculateAmountBeforeTax(amount, taxRate);

      return {
        ...lineItem,
        totalAmountBeforeTax: priceBeforeTax,
        totalAmountAfterTax: taxableAmount,
        totalAmount: usePriceBeforeTax ? priceBeforeTax : amount,
      };
    });
  }

  static calculateLineItemsAndTotals({
    lineItems,
    usePriceBeforeTax = false,
  }: {
    lineItems: LineEntryForm[] | LineEntry[];
    usePriceBeforeTax?: boolean;
    strategy?: Strategy;
  }) {
    const newLineItems = this.calculateLineItems({
      lineItems,
      usePriceBeforeTax,
    });

    return {
      lineItems: newLineItems,
      totals: this.calculateTotals({ lineItems: newLineItems }),
    };
  }

  // QB Publishing calculation
  static recalculateLineItemsAmount(lineItems: LineEntryForm[] | LineEntry[]) {
    return lineItems.map((lineItem) => {
      const amount = parseNumber(lineItem.totalAmount || 0);
      const taxRate = super.getTaxRate(lineItem.taxCode);
      const taxableAmount = this.calculateTaxableAmount(amount, taxRate);
      // 7 is derived from brute force testing
      const formattedTaxableAmount = toFixedWithoutRounding(taxableAmount, 7);
      return {
        ...lineItem,
        totalAmount: formattedTaxableAmount,
      };
    });
  }
}
